import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

@customElement('multi-select-field')
export class MultiSelectField extends LitElement {
  @property({ type: String }) name = '';
  @property({ type: String }) label = '';
  @property({ type: Array }) value: string[] = [];
  @property({ type: Array }) options: string[] = [];
  @property({ type: Boolean }) required = false;
  @property({ type: String }) error = '';
  @property({ type: Boolean }) touched = false;

  static styles = css`
    :host {
      display: block;
    }
    .field-group {
      margin-bottom: 16px;
    }
    .field-label {
      display: block;
      margin-bottom: 10px;
      font-weight: 600;
      color: #1f2937;
      font-size: 14px;
      letter-spacing: -0.01em;
    }
    .required {
      color: #ef4444;
      margin-left: 2px;
    }
    .options-container {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      padding: 16px;
      border: 1.5px solid #e5e7eb;
      border-radius: 10px;
      background: #fafbfc;
      min-height: 52px;
      transition: all 0.2s;
    }
    .options-container:hover {
      border-color: #cbd5e1;
      background: #f9fafb;
    }
    .options-container:focus-within {
      border-color: #6366f1;
      background: #ffffff;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
    }

    /* Badge/Pill Style */
    .option-badge {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      border-radius: 20px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      user-select: none;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      border: 2px solid transparent;
      background: #ffffff;
      color: #6b7280;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }

    .option-badge:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
      border-color: #e5e7eb;
      color: #374151;
    }

    .option-badge.selected {
      background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
      color: #ffffff;
      border-color: #4f46e5;
      box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
      font-weight: 600;
    }

    .option-badge.selected:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(79, 70, 229, 0.4);
    }

    .option-badge:active {
      transform: scale(0.96);
    }

    /* Checkmark icon for selected state */
    .option-badge.selected::before {
      content: '✓';
      font-size: 16px;
      font-weight: 700;
      line-height: 1;
      animation: checkPop 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    }

    @keyframes checkPop {
      0% {
        transform: scale(0) rotate(-45deg);
        opacity: 0;
      }
      50% {
        transform: scale(1.3) rotate(5deg);
      }
      100% {
        transform: scale(1) rotate(0deg);
        opacity: 1;
      }
    }

    .option-text {
      line-height: 1;
    }

    .error-message {
      color: #dc2626;
      font-size: 12px;
      margin-top: 8px;
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 500;
      animation: slideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .error-message::before {
      content: '⚠';
      font-size: 14px;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Empty state hint */
    .empty-hint {
      color: #9ca3af;
      font-size: 13px;
      font-style: italic;
    }

    @media (prefers-reduced-motion: reduce) {
      * {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;

  private _handleToggle(option: string) {
    if (this.value.includes(option)) {
      this.value = this.value.filter((v) => v !== option);
    } else {
      this.value = [...this.value, option];
    }
    this._emitChange();
  }

  private _emitChange() {
    this.dispatchEvent(
      new CustomEvent('field-change', {
        detail: { name: this.name, value: this.value },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _handleBlur() {
    this.dispatchEvent(
      new CustomEvent('field-blur', {
        detail: { name: this.name },
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    const hasError = this.error && this.touched;
    const hasOptions = this.options.length > 0;

    return html`
      <div class="field-group">
        ${this.label
          ? html`
              <label class="field-label">
                ${this.label}
                ${this.required ? html`<span class="required">*</span>` : ''}
              </label>
            `
          : ''}
        <div class="options-container" @blur=${this._handleBlur}>
          ${hasOptions
            ? this.options.map((option) => {
                const isSelected = this.value.includes(option);
                return html`
                  <button
                    type="button"
                    class="option-badge ${isSelected ? 'selected' : ''}"
                    @click=${() => this._handleToggle(option)}
                    aria-pressed=${isSelected}
                    role="checkbox"
                    aria-checked=${isSelected}>
                    <span class="option-text">${option}</span>
                  </button>
                `;
              })
            : html` <span class="empty-hint">No options available</span> `}
        </div>
        ${hasError
          ? html` <div class="error-message">${this.error}</div> `
          : ''}
      </div>
    `;
  }
}
